import {
  AfterViewChecked,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
  signal,
} from '@angular/core';

import { EditableCellConfig } from '../../models';

export interface CellEditEvent<T> {
  item: T;
  field: keyof T;
  value: string;
  previousValue: string;
}

export interface CellNavigationEvent<T> {
  direction: 'horizontal' | 'vertical';
  reverse: boolean;
  item: T;
  field: keyof T;
}

@Directive({
  selector: '[mdwEditableCell]',
  host: {
    '[class.editable-cell]': 'editingEnabled()',
    '[class.editable-cell--editing]': 'isEditing()',
    '(dblclick)': 'onDoubleClick($event)',
  },
})
export class MdwEditableCell<T> implements OnDestroy, AfterViewChecked {
  private elementRef = inject<ElementRef<HTMLTableCellElement>>(ElementRef);
  private renderer = inject(Renderer2);

  public item = input.required<T>();
  public columnName = input.required<keyof T>();
  public editConfig = input<EditableCellConfig<T>>();
  public trackBy = input<(index: number, item: T) => unknown>();

  public cellSaved = output<CellEditEvent<T>>();
  public tabToNextCell = output<CellNavigationEvent<T>>();

  public isEditing = signal<boolean>(false);

  public editingEnabled = computed(() => {
    const config = this.editConfig();

    if (!config) {
      return false;
    }

    return config.canEdit ? config.canEdit(this.item()) : true;
  });

  private originalValue = '';
  private currentEditValue = '';
  private inputElement: HTMLInputElement | null = null;
  private documentMouseDownListener: (() => void) | null = null;
  private lastItemIdentity: unknown = null;
  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public constructor() {
    // Watch for item changes (virtual scroll recycling) and cancel any active edit
    // We use trackBy to detect when a different item is assigned to this row,
    // not just when the same item's data is updated
    effect(() => {
      const currentItem = this.item();
      const trackByFn = this.trackBy();
      const currentIdentity = trackByFn ? trackByFn(0, currentItem) : currentItem;
      if (this.lastItemIdentity !== null && this.lastItemIdentity !== currentIdentity) {
        // Item identity changed - row was recycled by virtual scroll to show a different item
        if (this.isEditing()) {
          this.cancelEdit();
        }
      }
      this.lastItemIdentity = currentIdentity;
    });
  }

  public ngOnDestroy(): void {
    if (this.pendingTimeoutId !== null) {
      clearTimeout(this.pendingTimeoutId);
    }
    if (this.documentMouseDownListener) {
      this.documentMouseDownListener();
    }
    if (this.inputElement) {
      this.inputElement.remove();
    }
  }

  public ngAfterViewChecked(): void {
    // If we're supposed to be editing but input was removed (data update re-rendered the row),
    // re-create the input to maintain edit mode
    if (this.isEditing() && this.inputElement && !this.inputElement.isConnected) {
      this.createInputElement(this.currentEditValue, false);
    }
  }

  public onDoubleClick(event: Event): void {
    if (!this.editingEnabled() || this.isEditing()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.enterEditMode();
  }

  public enterEditMode(): void {
    if (this.isEditing()) {
      return;
    }

    const currentValue = String(this.item()[this.columnName()] ?? '');
    this.originalValue = currentValue;
    this.currentEditValue = currentValue;

    this.createInputElement(currentValue, true);
    this.isEditing.set(true);

    const cell = this.elementRef.nativeElement;
    this.pendingTimeoutId = setTimeout(() => {
      this.pendingTimeoutId = null;
      this.documentMouseDownListener = this.renderer.listen(
        'document',
        'mousedown',
        (e: MouseEvent) => {
          if (!cell.contains(e.target as Node)) {
            this.saveAndExit();
          }
        },
      );
    }, 0);
  }

  public exitEditMode(save: boolean): void {
    if (!this.isEditing()) {
      return;
    }

    const newValue = this.inputElement?.value ?? this.originalValue;

    if (this.documentMouseDownListener) {
      this.documentMouseDownListener();
      this.documentMouseDownListener = null;
    }

    // Just remove the input - Angular manages the underlying content
    if (this.inputElement) {
      this.inputElement.remove();
      this.inputElement = null;
    }

    // Emit save event if value changed
    if (save && newValue !== this.originalValue) {
      this.cellSaved.emit({
        item: this.item(),
        field: this.columnName(),
        value: newValue,
        previousValue: this.originalValue,
      });
    }

    this.isEditing.set(false);
  }

  private saveAndExit(): void {
    this.exitEditMode(true);
  }

  private handleKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.exitEditMode(true);
        this.tabToNextCell.emit({
          direction: 'vertical',
          reverse: event.shiftKey,
          item: this.item(),
          field: this.columnName(),
        });
        break;

      case 'Tab':
        event.preventDefault();
        this.exitEditMode(true);
        this.tabToNextCell.emit({
          direction: 'horizontal',
          reverse: event.shiftKey,
          item: this.item(),
          field: this.columnName(),
        });
        break;

      case 'Escape':
        event.preventDefault();
        this.exitEditMode(false);
        break;
    }
  }
  private createInputElement(value: string, selectAll: boolean): void {
    const cell = this.elementRef.nativeElement;

    this.inputElement = this.renderer.createElement('input') as HTMLInputElement;
    this.inputElement.type = 'text';
    this.inputElement.value = value;
    this.inputElement.className = 'editable-input';
    this.inputElement.placeholder = this.editConfig()?.placeholder ?? '';

    this.inputElement.addEventListener('keydown', this.handleKeydown.bind(this));
    this.inputElement.addEventListener('input', this.handleInput.bind(this));

    cell.appendChild(this.inputElement);

    setTimeout(() => {
      this.inputElement?.focus();
      if (selectAll) {
        this.inputElement?.select();
      } else {
        const len = this.inputElement?.value.length ?? 0;
        this.inputElement?.setSelectionRange(len, len);
      }
    });
  }

  private cancelEdit(): void {
    if (this.documentMouseDownListener) {
      this.documentMouseDownListener();
      this.documentMouseDownListener = null;
    }
    if (this.inputElement) {
      this.inputElement.remove();
      this.inputElement = null;
    }
    this.isEditing.set(false);
  }

  private handleInput(): void {
    this.currentEditValue = this.inputElement?.value ?? '';
  }
}
